import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Table, Button, Space, Tag, Select, Typography, message, Popconfirm, Switch, Input, Row, Col, EmptyState } from '../../ui';
import { Plus, Trash2, User, Star } from 'lucide-react';
import { request } from '../../api/client';

interface Warehouse { id: string; name: string; code: string; isMaster?: boolean }
interface Assignment {
  id: string; userId: string; warehouseId: string;
  userName?: string | null; userMobile?: string | null;
  warehouseName?: string | null; warehouseCode?: string | null;
  isPrimary: boolean; isActive: boolean; note?: string | null;
}

/**
 * Admin page to bind Warehouse Managers to the warehouses they operate (gated by
 * WAREHOUSE.MANAGER.ASSIGN). A WM is then scoped to those warehouses for stock, transfers,
 * packing and dispatch (WarehouseScopeService on the backend).
 */
export function WarehouseManagersPage({ embedded }: { embedded?: boolean } = {}) {
  const qc = useQueryClient();
  const [warehouseId, setWarehouseId] = useState<string>();
  const [userId, setUserId] = useState<string>();
  const [isPrimary, setIsPrimary] = useState(false);
  const [note, setNote] = useState('');

  const { data: warehouses } = useQuery({ queryKey: ['warehouses-all'], queryFn: () => request<Warehouse[]>({ url: '/warehouses' }) });
  const { data: wmUsers } = useQuery({
    queryKey: ['wm-users'],
    queryFn: () => request<any>({ url: '/users', params: { role: 'WAREHOUSE_MANAGER', pageSize: 200 } }),
  });
  const { data: assignments, isLoading } = useQuery({
    queryKey: ['warehouse-assignments', warehouseId],
    queryFn: () => request<Assignment[]>({ url: '/warehouse-assignments', params: warehouseId ? { warehouseId } : {} }),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['warehouse-assignments'] });
  const fail = (e: any) => message.error(e.response?.data?.error?.message ?? 'Something went wrong');

  const assignMut = useMutation({
    mutationFn: () => request({ url: '/warehouse-assignments', method: 'POST', data: { userId, warehouseId, isPrimary, note: note.trim() || undefined } }),
    onSuccess: () => { invalidate(); setUserId(undefined); setIsPrimary(false); setNote(''); message.success('Manager assigned'); },
    onError: fail,
  });
  const removeMut = useMutation({
    mutationFn: (id: string) => request({ url: `/warehouse-assignments/${id}`, method: 'DELETE' }),
    onSuccess: () => { invalidate(); message.success('Manager removed'); },
    onError: fail,
  });

  const whLabel = (id?: string) => {
    const w = (warehouses ?? []).find((x) => String(x.id) === String(id));
    return w ? `${w.name} (${w.code})` : id;
  };

  const columns = [
    {
      title: 'Manager',
      render: (_: any, a: Assignment) => (
        <div>
          <div style={{ fontWeight: 600 }}>
            {a.userName ?? `#${a.userId}`}
            {a.isPrimary && <Tag tone="green" style={{ marginLeft: 6 }}><Star size={16} /> Primary</Tag>}
          </div>
          <div style={{ fontSize: 12, color: '#888' }}>{a.userMobile ?? ''}</div>
        </div>
      ),
    },
    { title: 'Warehouse', render: (_: any, a: Assignment) => `${a.warehouseName ?? ''}${a.warehouseCode ? ` (${a.warehouseCode})` : ''}` },
    { title: 'Note', dataIndex: 'note', render: (v: string) => v || '—' },
    {
      title: '', align: 'right' as const, width: 60,
      render: (_: any, a: Assignment) => (
        <Popconfirm title="Remove this manager from the warehouse?" onConfirm={() => removeMut.mutate(a.id)} okButtonProps={{ danger: true }}>
          <Button size="sm" variant="danger" icon={<Trash2 size={16} />} aria-label="Remove manager" />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      {!embedded && <Typography.Title level={4}><User size={16} style={{ display: 'inline', verticalAlign: '-2px' }} /> Warehouse Managers</Typography.Title>}
      <Typography.Paragraph type="secondary">
        Assign warehouse managers to the warehouses they operate. A manager only sees and acts on stock, transfers,
        packing and dispatch for their assigned warehouse(s).
      </Typography.Paragraph>

      <Card title="Assign a manager" className="mb-4">
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={7}>
            <Select showSearch optionFilterProp="label" placeholder="Warehouse" className="w-full"
              value={warehouseId} onChange={setWarehouseId}
              options={(warehouses ?? []).map((w) => ({ value: String(w.id), label: `${w.name} (${w.code})` }))} />
          </Col>
          <Col xs={24} md={7}>
            <Select showSearch optionFilterProp="label" placeholder="Warehouse Manager (user)" className="w-full"
              value={userId} onChange={setUserId}
              options={(wmUsers?.data ?? []).map((u: any) => ({ value: String(u.id), label: `${u.fullName} · ${u.mobile}` }))}
              notFoundContent="No users with the Warehouse Manager role" />
          </Col>
          <Col xs={12} md={3}><Space><Switch checked={isPrimary} onCheckedChange={setIsPrimary} /> Primary</Space></Col>
          <Col xs={12} md={5}><Input placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} /></Col>
          <Col xs={24} md={2}>
            <Button variant="primary" icon={<Plus size={16} />} block disabled={!warehouseId || !userId}
              loading={assignMut.isPending} onClick={() => assignMut.mutate()}>Assign</Button>
          </Col>
        </Row>
      </Card>

      <Card title={<span>Assignments{warehouseId ? ` · ${whLabel(warehouseId)}` : ' (all warehouses)'}</span>}>
        <Table<Assignment> rowKey="id" loading={isLoading} dataSource={assignments ?? []} columns={columns} pagination={false}
          locale={{ emptyText: <EmptyState title="No managers assigned yet" /> }} />
      </Card>
    </div>
  );
}
